import React, { createContext, useContext, ReactNode } from "react";

/**
 * Snapshot view extensions — generic mechanism that lets project code
 * inject custom UI (a chart, a map, a per-instance metric) into the
 * autogenerated snapshot `{EntityName}View` components without the
 * generator (or the haiku DSL) knowing about it.
 *
 * Each entity name maps to a renderer that receives the snapshot data
 * for that entity and returns React content.
 *
 * Positioning: an entry can be a bare renderer (rendered at the BOTTOM
 * of the autogenerated view, below its standard FormControl blocks —
 * the default, backward-compatible behavior) or an object that places
 * content at `top` (above the standard blocks) and/or `bottom`. Both
 * slots render above the view's modal stack so augmentations never
 * overlap drilldown modals.
 *
 * Providers can be nested: a child Provider merges its extensions on
 * top of the parent's, so nested snapshot hosts can extend or override
 * augmentations without rewriting the full map.
 */

export type SnapshotExtensionRenderer = (snapshotData: any) => ReactNode;

/**
 * Bare renderer = bottom slot (back-compat). Object form positions
 * content at top and/or bottom of the snapshot view.
 */
export type SnapshotExtensionEntry =
  | SnapshotExtensionRenderer
  | {
      top?: SnapshotExtensionRenderer;
      bottom?: SnapshotExtensionRenderer;
    };

export type SnapshotExtensions = Record<string, SnapshotExtensionEntry>;

const SnapshotExtensionsContext = createContext<SnapshotExtensions>({});

export type SnapshotExtensionsProviderProps = {
  extensions: SnapshotExtensions;
  children: ReactNode;
};

export const SnapshotExtensionsProvider: React.FC<SnapshotExtensionsProviderProps> = ({
  extensions,
  children,
}) => {
  const parent = useContext(SnapshotExtensionsContext);
  const merged: SnapshotExtensions = { ...parent, ...extensions };
  return (
    <SnapshotExtensionsContext.Provider value={merged}>
      {children}
    </SnapshotExtensionsContext.Provider>
  );
};

function resolveSlot(
  entry: SnapshotExtensionEntry | undefined,
  slot: "top" | "bottom"
): SnapshotExtensionRenderer | undefined {
  if (!entry) return undefined;
  // A bare function renders at the bottom (backward-compatible default).
  if (typeof entry === "function") {
    return slot === "bottom" ? entry : undefined;
  }
  return entry[slot];
}

export function useSnapshotExtension(
  entityName: string
): SnapshotExtensionRenderer | undefined {
  const extensions = useContext(SnapshotExtensionsContext);
  return resolveSlot(extensions[entityName], "bottom");
}

export function useSnapshotExtensionTop(
  entityName: string
): SnapshotExtensionRenderer | undefined {
  const extensions = useContext(SnapshotExtensionsContext);
  return resolveSlot(extensions[entityName], "top");
}
